import { isObjectLike } from '@scalar/helpers/object/is-object';
import { traverse } from '../helpers/traverse.js';
const DEFAULT_MEDIA_TYPE = 'application/json';
/** Extracts and removes x-example and x-examples extensions from an object */
function extractXExampleExtensions(obj) {
    const xExample = obj['x-example'];
    const xExamples = obj['x-examples'];
    delete obj['x-example'];
    delete obj['x-examples'];
    return { xExample, xExamples };
}
/** Checks if a value is a non-null, non-array object with at least one entry */
function isNonEmptyObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length > 0;
}
/**
 * Checks if a value looks like a collection of named examples (all values are objects).
 * This helps distinguish between:
 * - A single example: { message: 'OK', type: 'success' } - values are primitives
 * - Named examples: { 'my-example': { message: 'OK' } } - values are objects
 */
function isNamedExamplesCollection(value) {
    return (isNonEmptyObject(value) &&
        Object.values(value).every((v) => typeof v === 'object' && v !== null && !Array.isArray(v)));
}
/** Checks if a schema is empty (no meaningful properties defined) */
function isEmptySchema(schema) {
    if (!isObjectLike(schema)) {
        return true;
    }
    const s = schema;
    const substantiveValidationKeywords = [
        'enum',
        'const',
        'not',
        'format',
        'multipleOf',
        'maximum',
        'exclusiveMaximum',
        'minimum',
        'exclusiveMinimum',
        'maxLength',
        'minLength',
        'pattern',
        'maxItems',
        'minItems',
        'uniqueItems',
        'maxProperties',
        'minProperties',
        'required',
    ];
    // Has substantive schema keywords — not empty
    if (s.allOf ||
        s.oneOf ||
        s.anyOf ||
        s.items ||
        s.$ref ||
        'additionalProperties' in s ||
        substantiveValidationKeywords.some((key) => key in s)) {
        return false;
    }
    // Has properties with at least one defined property — not empty
    if (typeof s.properties === 'object' && s.properties !== null && Object.keys(s.properties).length > 0) {
        return false;
    }
    return true;
}
/**
 * Removes content entries that only have an empty schema (no example/examples)
 * when other entries with actual examples exist. This prevents the UI from
 * selecting a meaningless produces-based entry over one with a real example.
 */
function removeEmptySchemaOnlyContentEntries(content) {
    const keys = Object.keys(content);
    const hasEntryWithExample = keys.some((key) => {
        const entry = content[key];
        return isObjectLike(entry) && (entry.example !== undefined || entry.examples !== undefined);
    });
    if (!hasEntryWithExample) {
        return;
    }
    for (const key of keys) {
        const entry = content[key];
        if (!isObjectLike(entry))
            continue;
        const hasExample = entry.example !== undefined || entry.examples !== undefined;
        const hasOnlySchema = entry.schema !== undefined && !hasExample && Object.keys(entry).length === 1;
        if (hasOnlySchema && isEmptySchema(entry.schema)) {
            delete content[key];
        }
    }
}
/** The allowed properties for an OpenAPI 3.x ExampleObject */
const EXAMPLE_OBJECT_PROPERTIES = new Set(['summary', 'description', 'value', 'externalValue']);
/**
 * Checks if a value is a valid OpenAPI 3.x ExampleObject.
 *
 * An ExampleObject must have a `value` (or `externalValue`) property and can only contain
 * properties from the allowed set: `summary`, `description`, `value`, `externalValue`.
 *
 * This prevents false positives when user's example data happens to have a `value` property
 * (e.g., `{ value: "some data", count: 5 }` should NOT be treated as an ExampleObject).
 */
function isExampleObject(value) {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const obj = value;
    const hasValueOrExternalValue = 'value' in obj || 'externalValue' in obj;
    const onlyHasAllowedProperties = Object.keys(obj).every((key) => EXAMPLE_OBJECT_PROPERTIES.has(key));
    return hasValueOrExternalValue && onlyHasAllowedProperties;
}
/** Wraps a value as an ExampleObject, preserving existing structure if valid */
function wrapAsExampleObject(value) {
    if (isExampleObject(value)) {
        return value;
    }
    return { value };
}
/**
 * True if the key looks like a MIME media type (e.g. application/json, text/plain).
 * Used to distinguish media-type example keys from named example keys when migrating
 * Swagger 2.0 examples to OpenAPI 3.0 content. The type must be a registered top-level
 * type (or `*`) and the subtype a single token, so named keys that contain a slash
 * ("Error 404/Not Found", "Coupons/Promos") stay named examples. Matching is
 * case-sensitive for undeclared keys so "Image/Before" stays a name. Explicit
 * consumes/produces entries take precedence, including mixed-case or private types.
 */
const MEDIA_TYPE_KEY_PATTERN = /^(\*|application|audio|example|font|haptics|image|message|model|multipart|text|video)\/[a-zA-Z0-9*+.-]+$/;
const isMediaTypeKey = (key, declaredMediaTypes) => {
    return declaredMediaTypes.includes(key) || MEDIA_TYPE_KEY_PATTERN.test(key);
};
/** Transforms x-example entries to OpenAPI 3.x examples format */
function transformXExampleToExamples(xExample) {
    return Object.entries(xExample).reduce((acc, [key, value]) => {
        acc[key] = { value };
        return acc;
    }, {});
}
/** Update the flow names to OpenAPI 3.1.0 format */
const upgradeFlow = (flow) => {
    switch (flow) {
        case 'application':
            return 'clientCredentials';
        case 'accessCode':
            return 'authorizationCode';
        case 'implicit':
            return 'implicit';
        case 'password':
            return 'password';
        default:
            return flow;
    }
};
/**
 * Upgrade Swagger 2.0 to OpenAPI 3.0
 *
 * https://swagger.io/blog/news/whats-new-in-openapi-3-0/
 */
export function upgradeFromTwoToThree(originalSpecification) {
    let document = originalSpecification;
    // Version
    if (document !== null &&
        typeof document === 'object' &&
        typeof document.swagger === 'string' &&
        document.swagger?.startsWith('2.0')) {
        document.openapi = '3.0.4';
        delete document.swagger;
    }
    else {
        // Skip if it's something else than 3.0.x
        return document;
    }
    // Servers
    if (document.host) {
        const schemes = Array.isArray(document.schemes) && document.schemes?.length ? document.schemes : ['http'];
        document.servers = schemes.map((scheme) => ({
            url: `${scheme}://${document.host}${document.basePath ?? ''}`,
        }));
        delete document.basePath;
        delete document.schemes;
        delete document.host;
    }
    else if (document.basePath) {
        document.servers = [{ url: document.basePath }];
        delete document.basePath;
    }
    // Schemas
    if (document.definitions) {
        for (const schema of Object.values(document.definitions)) {
            migrateSchemaNullability(schema);
        }
        document.components = Object.assign({}, document.components, {
            schemas: document.definitions,
        });
        delete document.definitions;
        // Rewrite $refs to definitions
        document = traverse(document, (schema) => {
            // Rewrite $refs to components
            if (typeof schema.$ref === 'string' && schema.$ref.startsWith('#/definitions/')) {
                schema.$ref = schema.$ref.replace(/^#\/definitions\//, '#/components/schemas/');
            }
            return schema;
        });
    }
    // Transform file type to string with binary format
    document = traverse(document, (schema) => {
        if (schema.type === 'file') {
            schema.type = 'string';
            schema.format = 'binary';
        }
        return schema;
    });
    if (Object.hasOwn(document, 'parameters')) {
        // update all the $refs before we do any transformations
        document = traverse(document, (schema) => {
            if (typeof schema.$ref === 'string' && schema.$ref.startsWith('#/parameters/')) {
                const schemaName = schema.$ref.split('/')[2];
                if (!schemaName) {
                    return schema;
                }
                const param = isObjectLike(document.parameters) && schemaName in document.parameters
                    ? document.parameters[schemaName]
                    : undefined;
                if (param && typeof param === 'object' && 'in' in param && (param.in === 'body' || param.in === 'formData')) {
                    schema.$ref = schema.$ref.replace(/^#\/parameters\//, '#/components/requestBodies/');
                }
                else {
                    schema.$ref = schema.$ref.replace(/^#\/parameters\//, '#/components/parameters/');
                }
            }
            return schema;
        });
        document.components ??= {};
        const params = {};
        const bodyParams = {};
        const parameters = isObjectLike(document.parameters) ? document.parameters : {};
        for (const [name, param] of Object.entries(parameters)) {
            if (param && typeof param === 'object') {
                // Handle reference objects
                if ('$ref' in param) {
                    const convertedParam = transformParameterObject(param);
                    params[name] = convertedParam;
                }
                else if ('in' in param) {
                    if (param.in === 'body') {
                        bodyParams[name] = migrateBodyParameter(param, document.consumes ?? [DEFAULT_MEDIA_TYPE]);
                    }
                    else if (param.in === 'formData') {
                        bodyParams[name] = migrateFormDataParameter([param], document.consumes);
                    }
                    else {
                        const convertedParam = transformParameterObject(param);
                        params[name] = convertedParam;
                    }
                }
            }
        }
        if (Object.keys(params).length > 0) {
            ;
            document.components.parameters = params;
        }
        if (Object.keys(bodyParams).length > 0) {
            ;
            document.components.requestBodies = bodyParams;
        }
        delete document.parameters;
    }
    // Handle global responses defined in #/responses
    if (Object.hasOwn(document, 'responses') && typeof document.responses === 'object' && document.responses !== null) {
        // Update all $refs from #/responses/ to #/components/responses/
        document = traverse(document, (schema) => {
            if (typeof schema.$ref === 'string' && schema.$ref.startsWith('#/responses/')) {
                schema.$ref = schema.$ref.replace(/^#\/responses\//, '#/components/responses/');
            }
            return schema;
        });
        document.components ??= {};
        const migratedResponses = {};
        const responses = document.responses;
        for (const [name, response] of Object.entries(responses)) {
            if (isObjectLike(response)) {
                // Handle reference objects
                if ('$ref' in response) {
                    migratedResponses[name] = response;
                }
                else {
                    // Transform the response object
                    const responseObj = response;
                    const produces = document.produces ?? [DEFAULT_MEDIA_TYPE];
                    // Transform schema to content
                    if (responseObj.schema) {
                        migrateSchemaNullability(responseObj.schema);
                        if (typeof responseObj.content !== 'object') {
                            responseObj.content = {};
                        }
                        for (const type of produces) {
                            ;
                            responseObj.content[type] = {
                                schema: responseObj.schema,
                            };
                        }
                        delete responseObj.schema;
                    }
                    // Transform examples from Swagger 2.0 to OpenAPI 3.0 format
                    if (responseObj.examples && typeof responseObj.examples === 'object') {
                        if (typeof responseObj.content !== 'object') {
                            responseObj.content = {};
                        }
                        const defaultMediaType = produces[0] ?? DEFAULT_MEDIA_TYPE;
                        for (const [key, exampleValue] of Object.entries(responseObj.examples)) {
                            // Media-type keys (e.g. application/json) go to content[key]; named example keys go to content[defaultMediaType].examples[key].
                            if (isMediaTypeKey(key, produces)) {
                                if (typeof responseObj.content[key] !== 'object') {
                                    ;
                                    responseObj.content[key] = {};
                                }
                                ;
                                responseObj.content[key].example =
                                    exampleValue;
                            }
                            else {
                                if (typeof responseObj.content[defaultMediaType] !== 'object') {
                                    ;
                                    responseObj.content[defaultMediaType] = {};
                                }
                                const mediaEntry = responseObj.content[defaultMediaType];
                                if (typeof mediaEntry.examples !== 'object') {
                                    mediaEntry.examples = {};
                                }
                                ;
                                mediaEntry.examples[key] =
                                    wrapAsExampleObject(exampleValue);
                            }
                        }
                        delete responseObj.examples;
                    }
                    // Clean up content entries that only have an empty schema when other entries have examples
                    if (responseObj.content && typeof responseObj.content === 'object') {
                        removeEmptySchemaOnlyContentEntries(responseObj.content);
                    }
                    // Transform headers if present
                    if (isObjectLike(responseObj.headers)) {
                        responseObj.headers = Object.entries(responseObj.headers).reduce((acc, [headerName, header]) => {
                            if (header && typeof header === 'object') {
                                return {
                                    [headerName]: transformResponseHeader(header),
                                    ...acc,
                                };
                            }
                            return acc;
                        }, {});
                    }
                    migratedResponses[name] = responseObj;
                }
            }
        }
        if (Object.keys(migratedResponses).length > 0) {
            ;
            document.components.responses = migratedResponses;
        }
        delete document.responses;
    }
    // Paths
    if (typeof document.paths === 'object') {
        for (const path in document.paths) {
            if (Object.hasOwn(document.paths, path)) {
                const pathItem = isObjectLike(document.paths) && path in document.paths ? document.paths[path] : undefined;
                if (!pathItem || typeof pathItem !== 'object') {
                    continue;
                }
                let requestBodyObject;
                for (const methodOrParameters in pathItem) {
                    if (methodOrParameters === 'parameters' && Object.hasOwn(pathItem, methodOrParameters)) {
                        const pathItemParameters = migrateParameters(pathItem.parameters, document.consumes ?? [DEFAULT_MEDIA_TYPE]);
                        pathItem.parameters = pathItemParameters.parameters;
                        requestBodyObject = pathItemParameters.requestBody;
                    }
                    else if (Object.hasOwn(pathItem, methodOrParameters)) {
                        const operationItem = pathItem[methodOrParameters];
                        if (requestBodyObject) {
                            operationItem.requestBody = requestBodyObject;
                        }
                        if (operationItem.parameters) {
                            const migrationResult = migrateParameters(operationItem.parameters, operationItem.consumes ?? document.consumes ?? [DEFAULT_MEDIA_TYPE]);
                            operationItem.parameters = migrationResult.parameters;
                            if (migrationResult.requestBody) {
                                operationItem.requestBody = migrationResult.requestBody;
                            }
                        }
                        delete operationItem.consumes;
                        // Responses
                        if (operationItem.responses) {
                            for (const response in operationItem.responses) {
                                if (Object.hasOwn(operationItem.responses, response)) {
                                    const responseItem = operationItem.responses[response];
                                    if (responseItem.headers && typeof responseItem.headers === 'object') {
                                        responseItem.headers = Object.entries(responseItem.headers).reduce((acc, [name, header]) => {
                                            if (header && typeof header === 'object') {
                                                return {
                                                    [name]: transformResponseHeader(header),
                                                    ...acc,
                                                };
                                            }
                                            return acc;
                                        }, {});
                                    }
                                    if (responseItem.schema) {
                                        migrateSchemaNullability(responseItem.schema);
                                        const produces = document.produces ?? operationItem.produces ?? [DEFAULT_MEDIA_TYPE];
                                        if (typeof responseItem.content !== 'object') {
                                            responseItem.content = {};
                                        }
                                        for (const type of produces) {
                                            responseItem.content[type] = {
                                                schema: responseItem.schema,
                                            };
                                        }
                                        delete responseItem.schema;
                                    }
                                    // Transform response examples from Swagger 2.0 to OpenAPI 3.0 format
                                    // In Swagger 2.0, examples are at response level: examples: { DEFAULT_MEDIA_TYPE: {...} } or named: { 'Example': {...} }
                                    // In OpenAPI 3.0, examples move inside content: content: { DEFAULT_MEDIA_TYPE: { example: {...} } } or examples: { Example: { value: {...} } }
                                    if (responseItem.examples && typeof responseItem.examples === 'object') {
                                        if (typeof responseItem.content !== 'object') {
                                            responseItem.content = {};
                                        }
                                        const produces = document.produces ?? operationItem.produces ?? [DEFAULT_MEDIA_TYPE];
                                        const defaultMediaType = produces[0] ?? DEFAULT_MEDIA_TYPE;
                                        for (const [key, exampleValue] of Object.entries(responseItem.examples)) {
                                            // Media-type keys (e.g. application/json) go to content[key]; named example keys go to content[defaultMediaType].examples[key].
                                            if (isMediaTypeKey(key, operationItem.produces ?? produces)) {
                                                if (typeof responseItem.content[key] !== 'object') {
                                                    responseItem.content[key] = {};
                                                }
                                                responseItem.content[key].example = exampleValue;
                                            }
                                            else {
                                                if (typeof responseItem.content[defaultMediaType] !== 'object') {
                                                    responseItem.content[defaultMediaType] = {};
                                                }
                                                const mediaEntry = responseItem.content[defaultMediaType];
                                                if (typeof mediaEntry.examples !== 'object') {
                                                    mediaEntry.examples = {};
                                                }
                                                mediaEntry.examples[key] = wrapAsExampleObject(exampleValue);
                                            }
                                        }
                                        delete responseItem.examples;
                                    }
                                    // Clean up content entries that only have an empty schema when other entries have examples
                                    if (responseItem.content && typeof responseItem.content === 'object') {
                                        removeEmptySchemaOnlyContentEntries(responseItem.content);
                                    }
                                }
                            }
                        }
                        delete operationItem.produces;
                        // Delete empty parameters
                        if (operationItem.parameters?.length === 0) {
                            delete operationItem.parameters;
                        }
                    }
                }
            }
        }
    }
    // Upgrade securityDefinitions
    if (document.securityDefinitions) {
        if (typeof document.components !== 'object' || document.components === null) {
            document.components = {};
        }
        if (document.components && typeof document.components === 'object') {
            Object.assign(document.components, { securitySchemes: {} });
        }
        for (const [key, securityScheme] of Object.entries(document.securityDefinitions)) {
            if (typeof securityScheme === 'object') {
                if ('type' in securityScheme && securityScheme.type === 'oauth2') {
                    const { flow, authorizationUrl, tokenUrl, scopes } = securityScheme;
                    // Convert flow values to OpenAPI 3.1.0 format
                    if (document.components &&
                        typeof document.components === 'object' &&
                        'securitySchemes' in document.components &&
                        document.components.securitySchemes) {
                        Object.assign(document.components.securitySchemes, {
                            [key]: {
                                type: 'oauth2',
                                flows: {
                                    [upgradeFlow(flow || 'implicit')]: Object.assign({}, authorizationUrl && { authorizationUrl }, tokenUrl && { tokenUrl }, scopes && { scopes }),
                                },
                            },
                        });
                    }
                }
                else if ('type' in securityScheme && securityScheme.type === 'basic') {
                    if (document.components &&
                        typeof document.components === 'object' &&
                        'securitySchemes' in document.components &&
                        document.components.securitySchemes) {
                        Object.assign(document.components.securitySchemes, {
                            [key]: {
                                type: 'http',
                                scheme: 'basic',
                            },
                        });
                    }
                }
                else {
                    if (document.components &&
                        typeof document.components === 'object' &&
                        'securitySchemes' in document.components &&
                        document.components.securitySchemes) {
                        Object.assign(document.components.securitySchemes, {
                            [key]: securityScheme,
                        });
                    }
                }
            }
        }
        delete document.securityDefinitions;
    }
    delete document.consumes;
    delete document.produces;
    return document;
}
/**
 * Swagger tooling uses x-nullable for the nullability keyword introduced in OpenAPI 3.0.
 * Visit only subschemas so example/default values and custom extensions remain user data.
 */
const migrateSchemaNullability = (schema) => {
    if (!isObjectLike(schema)) {
        return;
    }
    if (typeof schema['x-nullable'] === 'boolean') {
        // False is already the default in OpenAPI 3.0 and needs no keyword in later versions.
        if (schema['x-nullable']) {
            schema.nullable = true;
        }
        delete schema['x-nullable'];
    }
    if (isObjectLike(schema.properties)) {
        for (const property of Object.values(schema.properties)) {
            migrateSchemaNullability(property);
        }
    }
    migrateSchemaNullability(schema.items);
    migrateSchemaNullability(schema.additionalProperties);
    if (Array.isArray(schema.allOf)) {
        for (const member of schema.allOf) {
            migrateSchemaNullability(member);
        }
    }
};
function transformItemsObject(obj) {
    const schemaProperties = [
        'type',
        'format',
        'default',
        'items',
        'maximum',
        'exclusiveMaximum',
        'minimum',
        'exclusiveMinimum',
        'maxLength',
        'minLength',
        'pattern',
        'maxItems',
        'minItems',
        'uniqueItems',
        'enum',
        'multipleOf',
        'x-nullable',
    ];
    const schema = schemaProperties.reduce((acc, property) => {
        if (Object.hasOwn(obj, property)) {
            acc[property] = obj[property];
            delete obj[property];
        }
        return acc;
    }, {});
    migrateSchemaNullability(schema);
    return schema;
}
function getParameterLocation(location) {
    if (location === 'formData') {
        throw new Error('Encountered a formData parameter which should have been filtered out by the caller');
    }
    if (location === 'body') {
        throw new Error('Encountered a body parameter which should have been filtered out by the caller');
    }
    return location;
}
function transformParameterObject(parameter) {
    if (Object.hasOwn(parameter, '$ref') && typeof parameter.$ref === 'string') {
        return {
            $ref: parameter.$ref,
        };
    }
    // it is important to call getParameterSerializationStyle first because transformItemsObject modifies properties on which getParameterSerializationStyle rely on
    const serializationStyle = getParameterSerializationStyle(parameter);
    const schema = transformItemsObject(parameter);
    const { xExample, xExamples } = extractXExampleExtensions(parameter);
    // Input:
    // x-example:
    //   application/json:
    //     message: OK
    //     type: success
    //   text/plain: 'OK'
    // Output:
    // examples:
    //   application/json:
    //     value:
    //       message: OK
    //       type: success
    //   text/plain:
    //     value: 'OK'
    // We need to transform the x-example to an examples object and add "value" to the structure
    if (isNonEmptyObject(xExample)) {
        parameter.examples = transformXExampleToExamples(xExample);
    }
    else if (isNonEmptyObject(xExamples)) {
        parameter.examples = Object.entries(xExamples).reduce((acc, [key, exampleValue]) => {
            acc[key] = wrapAsExampleObject(exampleValue);
            return acc;
        }, {});
    }
    delete parameter.collectionFormat;
    delete parameter.default;
    if (!parameter.in) {
        throw new Error('Parameter object must have an "in" property');
    }
    return {
        schema,
        ...serializationStyle,
        ...parameter,
        in: getParameterLocation(parameter.in),
    };
}
/**
 * Transform OpenAPI 2.0 response header to OpenAPI 3.0 format.
 * Response headers do not have "in", "name", "style", or "explode" properties.
 */
function transformResponseHeader(header) {
    if (Object.hasOwn(header, '$ref') && typeof header.$ref === 'string') {
        return {
            $ref: header.$ref,
        };
    }
    const schema = transformItemsObject(header);
    return {
        ...header,
        schema,
    };
}
const querySerialization = {
    ssv: {
        style: 'spaceDelimited',
        explode: false,
    },
    pipes: {
        style: 'pipeDelimited',
        explode: false,
    },
    multi: {
        style: 'form',
        explode: true,
    },
    csv: {
        style: 'form',
        explode: false,
    },
    tsv: {},
};
const pathAndHeaderSerialization = {
    ssv: {},
    pipes: {},
    multi: {},
    csv: {
        style: 'simple',
        explode: false,
    },
    tsv: {},
};
const serializationStyles = {
    header: pathAndHeaderSerialization,
    query: querySerialization,
    path: pathAndHeaderSerialization,
};
function getParameterSerializationStyle(parameter) {
    if (parameter.type !== 'array' ||
        !(parameter.in === 'query' || parameter.in === 'path' || parameter.in === 'header')) {
        return {};
    }
    const collectionFormat = parameter.collectionFormat ?? 'csv';
    if (parameter.in in serializationStyles && collectionFormat in serializationStyles[parameter.in]) {
        return serializationStyles[parameter.in][collectionFormat];
    }
    return {};
}
/**
 * Translate the Swagger 2.0 `collectionFormat` of a formData array parameter into an OpenAPI 3.0
 * encoding entry. Form bodies serialize arrays with the same styles as query parameters, so the
 * query serialization mapping is reused. Returns undefined when there is nothing to preserve, for
 * example a non-array parameter, one without a `collectionFormat`, or `tsv` which has no OpenAPI
 * 3.0 equivalent.
 */
function getFormDataEncoding(parameter) {
    if (parameter.type !== 'array' || typeof parameter.collectionFormat !== 'string') {
        return undefined;
    }
    const encoding = querySerialization[parameter.collectionFormat];
    if (!encoding || Object.keys(encoding).length === 0) {
        return undefined;
    }
    return encoding;
}
function migrateBodyParameter(bodyParameter, consumes) {
    // Extract x-example and x-examples before deleting other properties
    // @see https://redocly.com/docs-legacy/api-reference-docs/specification-extensions/x-examples
    const { xExample, xExamples } = extractXExampleExtensions(bodyParameter);
    delete bodyParameter.name;
    delete bodyParameter.in;
    const { schema, ...requestBody } = bodyParameter;
    migrateSchemaNullability(schema);
    const requestBodyObject = {
        content: {},
        ...requestBody,
    };
    if (requestBodyObject.content) {
        for (const type of consumes) {
            requestBodyObject.content[type] = {
                schema: schema,
            };
            // Handle x-examples (plural) first - Redocly extension for Swagger 2.0
            // Transforms to OpenAPI 3.x `examples` field (named examples with summary/value).
            // When both x-example and x-examples exist, we prefer x-examples so each media type
            // has only one of `example` or `examples` (OpenAPI 3 does not use both on the same type).
            if (isNonEmptyObject(xExamples) && type in xExamples) {
                const examples = xExamples[type];
                // Check if examples is already in proper OpenAPI 3.x format (object with entries that are valid ExampleObjects)
                const isExamplesCollection = isNonEmptyObject(examples) && Object.values(examples).every((example) => isExampleObject(example));
                if (isExamplesCollection) {
                    requestBodyObject.content[type].examples = examples;
                }
                // Named examples without value wrappers - wrap each individually
                else if (isNamedExamplesCollection(examples)) {
                    requestBodyObject.content[type].examples = Object.entries(examples).reduce((acc, [key, exampleValue]) => {
                        acc[key] = wrapAsExampleObject(exampleValue);
                        return acc;
                    }, {});
                }
                // Single example value - wrap as default
                else {
                    requestBodyObject.content[type].examples = {
                        default: wrapAsExampleObject(examples),
                    };
                }
            }
            // Fallback: x-examples keyed by example name instead of media type
            // e.g. x-examples: { Request: { email: "test@example.com" } }
            // Decided per key, so a key that belongs to another media type never drops the named ones
            else if (isNonEmptyObject(xExamples)) {
                const namedExamples = Object.entries(xExamples).filter(([key]) => !isMediaTypeKey(key, consumes));
                if (namedExamples.length) {
                    requestBodyObject.content[type].examples = namedExamples.reduce((acc, [key, exampleValue]) => {
                        acc[key] = wrapAsExampleObject(exampleValue);
                        return acc;
                    }, {});
                }
            }
            // Handle x-example (singular) only when we did not set examples for this type
            // (avoids having both example and examples on the same media type)
            if (!requestBodyObject.content[type].examples && isNonEmptyObject(xExample) && type in xExample) {
                requestBodyObject.content[type].example = xExample[type];
            }
        }
    }
    return requestBodyObject;
}
function migrateFormDataParameter(parameters, consumes = ['multipart/form-data']) {
    const requestBodyObject = {
        content: {},
    };
    // Ensure consumes contains a form data content type
    const filtered = consumes.filter((type) => type === 'multipart/form-data' || type === 'application/x-www-form-urlencoded');
    const contentTypes = filtered.length > 0 ? filtered : ['multipart/form-data'];
    if (requestBodyObject.content) {
        for (const contentType of contentTypes) {
            requestBodyObject.content[contentType] = {
                schema: {
                    type: 'object',
                    properties: {},
                    required: [], // Initialize required array
                },
            };
            const formContent = requestBodyObject.content?.[contentType];
            if (formContent?.schema && typeof formContent.schema === 'object' && 'properties' in formContent.schema) {
                for (const param of parameters) {
                    if (param.name && formContent.schema.properties) {
                        formContent.schema.properties[param.name] = {
                            ...transformItemsObject(structuredClone(param)),
                            // Only carry the description over when it exists, so a parameter without one does not
                            // end up with an explicit `description: undefined` on its schema.
                            ...(param.description !== undefined ? { description: param.description } : {}),
                        };
                        // Preserve the array serialization hint (Swagger 2.0 `collectionFormat`) as an
                        // OpenAPI 3.0 encoding entry on the media type, so it is not silently dropped.
                        const encoding = getFormDataEncoding(param);
                        if (encoding) {
                            formContent.encoding ??= {};
                            formContent.encoding[param.name] = encoding;
                        }
                        // Add to required array if param is required
                        if (param.required && Array.isArray(formContent.schema.required)) {
                            formContent.schema.required.push(param.name);
                        }
                    }
                }
            }
        }
    }
    return requestBodyObject;
}
function migrateParameters(parameters, consumes) {
    const result = {
        parameters: parameters
            .filter((parameter) => !(parameter.in === 'body' || parameter.in === 'formData'))
            .map((parameter) => transformParameterObject(parameter)),
    };
    const bodyParameter = structuredClone(parameters.find((parameter) => parameter.in === 'body') ?? {});
    if (bodyParameter && Object.keys(bodyParameter).length) {
        result.requestBody = migrateBodyParameter(bodyParameter, consumes);
    }
    const formDataParameters = parameters.filter((parameter) => parameter.in === 'formData');
    if (formDataParameters.length > 0) {
        const requestBodyObject = migrateFormDataParameter(formDataParameters, consumes);
        if (typeof result.requestBody !== 'object') {
            result.requestBody = requestBodyObject;
        }
        else {
            result.requestBody = {
                ...result.requestBody,
                content: {
                    ...result.requestBody.content,
                    ...requestBodyObject.content,
                },
            };
        }
        if (typeof result.requestBody !== 'object') {
            result.requestBody = {
                content: {},
            };
        }
    }
    return result;
}
